import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, FileText, AlertTriangle, CheckCircle, ArrowRight, Clock, Truck, Globe, Info } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article7 = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white">
      <div className="container mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Badge className="bg-indigo-600 text-white mr-4">
              <FileText className="h-3 w-3 mr-1" />
              Border Compliance
            </Badge>
            <span className="text-sm text-gray-600">Trans.info / French Customs (Douane)</span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            April 2026
            <span className="mx-2">•</span>
            <span>6 min read</span>
          </div>
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.7.title')}
          </h1>

          <img
            src="https://cdn-trans.info/uploads/2025/10/33ca8cb471880b7a6c4e0d81896.webp"
            alt="UK-France Smart Border truck crossing"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.7.intro')}
            </p>

            {/* Urgent Deadline Banner */}
            <div className="bg-red-600 text-white p-5 rounded-lg my-8 flex items-start gap-4">
              <AlertTriangle className="h-8 w-8 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-lg mb-1">
                  {language === 'ko' ? '⚠️ 긴급 마감 기한: 2026년 4월 20일' : '⚠️ Hard Deadline: 20 April 2026'}
                </p>
                <p className="text-sm text-red-100">
                  {language === 'ko'
                    ? '이 날짜부터 ELO 없이는 영불 스마트 보더를 통과할 수 없습니다. 지금 바로 준비하세요.'
                    : 'From this date, no truck may cross the France-UK Smart Border without a valid ELO. Prepare now.'}
                </p>
              </div>
            </div>

            {/* Key Stats */}
            <div className="bg-indigo-50 p-6 rounded-lg my-8 border border-indigo-200">
              <h3 className="font-bold text-indigo-900 mb-4">
                {language === 'ko' ? '핵심 사항 요약' : 'Key Facts at a Glance'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="text-center bg-white rounded p-3">
                  <Clock className="h-5 w-5 mx-auto mb-1 text-red-600" />
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '시행일' : 'Mandatory From'}</p>
                  <p className="text-xl font-bold text-red-600">20 Apr 2026</p>
                </div>
                <div className="text-center bg-white rounded p-3">
                  <Truck className="h-5 w-5 mx-auto mb-1 text-orange-600" />
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '적용 대상' : 'Applies To'}</p>
                  <p className="text-base font-bold text-orange-600">{language === 'ko' ? '모든 트럭 (적재·공차)' : 'All trucks (loaded/empty)'}</p>
                </div>
                <div className="text-center bg-white rounded p-3">
                  <Globe className="h-5 w-5 mx-auto mb-1 text-indigo-600" />
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '주요 통로' : 'Key Corridors'}</p>
                  <p className="text-base font-bold text-indigo-600">Calais · Dunkirk · Tunnel</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.7.section1.title')}
            </h2>
            <p className="mb-6">{t('article.7.p1')}</p>
            <p className="mb-6">{t('article.7.p2')}</p>

            {/* Coverage Table */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Info className="mr-2 h-5 w-5 text-indigo-600" />
                {language === 'ko' ? 'ELO 적용 범위' : 'ELO Coverage Scope'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-100">
                      <th className="text-left py-2 px-3">{language === 'ko' ? '구분' : 'Category'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '세부 사항' : 'Detail'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '적용 여부' : 'Required?'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { cat: language === 'ko' ? '통관 유형' : 'Customs Type', detail: language === 'ko' ? '수입 (Import)' : 'Imports', req: true },
                      { cat: '', detail: language === 'ko' ? '수출 (Export)' : 'Exports', req: true },
                      { cat: '', detail: language === 'ko' ? '환적 (Transit)' : 'Transits', req: true },
                      { cat: language === 'ko' ? '차량 상태' : 'Vehicle Status', detail: language === 'ko' ? '적재 차량' : 'Loaded trucks', req: true },
                      { cat: '', detail: language === 'ko' ? '공차 차량' : 'Empty trucks', req: true },
                      { cat: language === 'ko' ? '운송 방식' : 'Transport Mode', detail: language === 'ko' ? '동반 (Accompanied)' : 'Accompanied RoRo', req: true },
                      { cat: '', detail: language === 'ko' ? '비동반 (Unaccompanied)' : 'Unaccompanied RoRo', req: true },
                      { cat: 'ELO 비율', detail: language === 'ko' ? '트럭당 ELO 수' : 'ELOs per truck', req: null, note: language === 'ko' ? '1대 = 1개' : '1 truck = 1 ELO' },
                    ].map((row, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2 px-3 font-medium text-gray-600">{row.cat}</td>
                        <td className="py-2 px-3">{row.detail}</td>
                        <td className="py-2 px-3">
                          {row.req === null
                            ? <span className="font-bold text-indigo-700">{row.note}</span>
                            : row.req
                              ? <span className="text-green-600 font-semibold flex items-center gap-1"><CheckCircle className="h-4 w-4" />{language === 'ko' ? '의무' : 'Mandatory'}</span>
                              : <span className="text-gray-400">{language === 'ko' ? '해당 없음' : 'N/A'}</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.7.section2.title')}
            </h2>
            <p className="mb-6">{t('article.7.p3')}</p>
            <p className="mb-6">{t('article.7.p4')}</p>

            {/* Step-by-step process */}
            <div className="bg-indigo-50 p-6 rounded-lg my-8 border border-indigo-200">
              <h3 className="font-bold text-indigo-900 mb-5 flex items-center">
                <ArrowRight className="mr-2 h-5 w-5" />
                {language === 'ko' ? '터미널 도착 전 필수 체크리스트' : 'Pre-Terminal Mandatory Checklist'}
              </h3>
              <div className="space-y-3">
                {[
                  {
                    step: '1',
                    title: language === 'ko' ? 'ICS2 안전·보안 신고 완료' : 'Complete ICS2 Safety & Security Filing',
                    desc: language === 'ko' ? '영불 국경 통과 전 ICS2 신고를 완료해야 합니다.' : 'ICS2 entry summary declaration must be completed before crossing.',
                  },
                  {
                    step: '2',
                    title: language === 'ko' ? 'ENS MRN 확보' : 'Obtain ENS MRN',
                    desc: language === 'ko' ? 'ICS2 신고 후 발급되는 ENS(Entry Summary Number) MRN을 확보합니다.' : 'Obtain the Movement Reference Number from the Entry Summary declaration.',
                  },
                  {
                    step: '3',
                    title: language === 'ko' ? 'ELO 생성 (ENS MRN 포함)' : 'Create ELO (Include ENS MRN)',
                    desc: language === 'ko' ? 'douane.gouv.fr 계정에서 ELO를 생성하고, 반드시 ENS MRN을 포함시킵니다.' : 'Create the ELO via douane.gouv.fr account, including the ENS MRN reference.',
                  },
                  {
                    step: '4',
                    title: language === 'ko' ? '바코드 준비' : 'Barcode Ready',
                    desc: language === 'ko' ? 'ELO 생성 후 발급되는 바코드를 체크인에서 제시할 수 있도록 준비합니다.' : 'Once ELO is created, the barcode is generated — this is the key document for check-in.',
                  },
                ].map((item) => (
                  <div key={item.step} className="flex items-start bg-white rounded p-3 gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">
                      {item.step}
                    </span>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                <strong>{language === 'ko' ? '⚠️ 중요: ' : '⚠️ Important: '}</strong>
                {language === 'ko'
                  ? '한 단계라도 누락되면 트럭이 국경에 도착할 수 있지만 정상적으로 진행하지 못할 수 있습니다.'
                  : 'If one link in the chain is missing, the truck can arrive at the crossing but may not be able to proceed normally.'}
              </div>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.7.section3.title')}
            </h2>
            <p className="mb-6">{t('article.7.p5')}</p>
            <p className="mb-6">{t('article.7.p6')}</p>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.7.section4.title')}
            </h2>
            <p className="mb-6">{t('article.7.p7')}</p>
            <p className="mb-6">{t('article.7.p8')}</p>

            {/* Action Steps Box */}
            <div className="bg-green-50 p-6 rounded-lg my-8 border border-green-200">
              <h3 className="font-bold text-green-900 mb-4 flex items-center">
                <CheckCircle className="mr-2 h-5 w-5" />
                {language === 'ko' ? '지금 당장 해야 할 일' : 'Action Steps — Do This Now'}
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{language === 'ko' ? 'douane.gouv.fr 개인 계정을 아직 개설하지 않았다면 즉시 생성하세요.' : 'Create a douane.gouv.fr personal account if you do not already have one.'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{language === 'ko' ? '포털에서 ELO 신청을 찾아 생성 프로세스를 테스트해 보세요.' : 'Find the ELO application in the portal and test the creation process.'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{language === 'ko' ? 'ICS2 신고 → ENS MRN 확보 → ELO 생성 → 바코드 준비 순서를 숙지하세요.' : 'Familiarise your team with the sequence: ICS2 → ENS MRN → ELO → barcode.'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{language === 'ko' ? '칼레·뒹케르크·채널 터널 이용 시 체크인 전 모든 준비를 완료했는지 확인하세요.' : 'For all crossings via Calais, Dunkirk or the Channel Tunnel — verify the full chain is complete before check-in.'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{language === 'ko' ? '공차 차량도 예외 없이 ELO가 필요합니다.' : 'Empty trucks are not exempt — they also require an ELO.'}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Trans.info / French Customs (Douane) — ELO mandatory from 20 April 2026.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a
              href="https://trans.info/en/elo-starts-20april-468091"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article7;
